import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Compass, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 8;

export default function PaymentResult({ variant }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(variant === "cancel" ? "cancelled" : "polling");
  const [record, setRecord] = useState(null);

  const poll = useCallback(async (attempt) => {
    if (attempt >= MAX_POLLS) { setState("timeout"); return; }
    try {
      const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
      setRecord(data);
      if (data.payment_status === "paid") { setState("success"); return; }
      if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
    } catch (e) { /* keep trying */ }
    setTimeout(() => poll(attempt + 1), 2000);
  }, [sessionId]);

  useEffect(() => {
    if (variant === "cancel") return;
    if (!sessionId) { setState("timeout"); return; }
    poll(0);
  }, [sessionId, variant, poll]);

  const cfg = {
    polling: { icon: <Loader2 className="w-12 h-12 text-brand-aqua animate-spin" />, title: "Confirming your payment…", body: "Hold tight while we verify with the payment provider." },
    success: { icon: <CheckCircle2 className="w-12 h-12 text-brand-teal" />, title: "Payment received", body: `Thank you — we've logged your payment${record?.item_name ? ` for ${record.item_name}` : ""}. We'll be in touch to confirm the details.` },
    failed: { icon: <XCircle className="w-12 h-12 text-brand-coral" />, title: "Payment not completed", body: "Something went wrong with the payment. No charge was made — please try again." },
    cancelled: { icon: <XCircle className="w-12 h-12 text-brand-coral" />, title: "Payment cancelled", body: "You cancelled the payment. Nothing was charged — you can pick it up again anytime." },
    timeout: { icon: <Loader2 className="w-12 h-12 text-brand-aqua" />, title: "Still processing", body: "This is taking longer than usual. If you completed payment, we'll have it recorded — please contact us if unsure." },
  }[state];

  return (
    <main className="min-h-screen bg-brand-navy flex flex-col" data-testid="payment-result-page">
      <div className="border-b border-brand-teal/20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-20 flex items-center">
          <a href="/" className="flex items-center gap-3">
            <Compass className="w-6 h-6 text-brand-aqua" />
            <span className="font-serif text-lg text-brand-sand">WayFinder <span className="text-brand-teal">Pacific</span></span>
          </a>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="max-w-lg w-full border border-brand-teal/20 p-10 text-center" data-testid={`payment-state-${state}`}>
          <div className="flex justify-center">{cfg.icon}</div>
          <h1 className="mt-6 font-serif text-3xl sm:text-4xl text-brand-sand tracking-tight">{cfg.title}</h1>
          <p className="mt-4 font-sans text-brand-sand/70 leading-relaxed">{cfg.body}</p>
          {record?.amount != null && state === "success" && (
            <p className="mt-4 font-serif text-2xl text-brand-teal">${Number(record.amount).toFixed(2)} USD</p>
          )}
          <button onClick={() => navigate("/")} data-testid="payment-back-home"
            className="mt-8 inline-flex items-center gap-2 px-6 py-3 border border-brand-teal text-brand-teal hover:bg-brand-teal hover:text-brand-navy transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to WayFinder
          </button>
        </div>
      </div>
    </main>
  );
}
